import { Liquid } from 'liquidjs';

const defaultLiquidTemplate = `
{% assign hasKeywords = response.keywords.hits.length > 0 %}
{% assign hasProducts = response.products.hits.length > 0 %}
{% assign hasHistory = history.length > 0 %}
{% assign imagePlaceHolder = 'https://cdn.nosto.com/nosto/9/mock' %}

<div class="ns-autocomplete-results">
  {% if hasKeywords == false and hasProducts == false and hasHistory %}
    <div class="ns-autocomplete-history">
      <div class="ns-autocomplete-header">
        Recently searched
      </div>
      {% for hit in history %}
        <div class="ns-autocomplete-history-item" data-ns-hit="{{ hit | json | escape }}" data-testid="history">
          {{ hit.item }}
          <a
            href="#"
            class="ns-autocomplete-history-item-remove"
            data-ns-remove-history="{{hit.item}}">
            &#x2715;
          </a>
        </div>
      {% endfor %}
    </div>
    <div class="ns-autocomplete-history-clear">
      <button
        type="button"
        class="ns-autocomplete-button"
        data-ns-remove-history="all">
        Clear history
      </button>
    </div>
  {% elsif hasKeywords or hasProducts %}
    {% if hasKeywords %}
      <div class="ns-autocomplete-keywords">
        <div class="ns-autocomplete-header">
          Keywords
        </div>
        {% for hit in response.keywords.hits %}
          <div class="ns-autocomplete-keyword" data-ns-hit="{{ hit | json | escape }}" data-testid="keyword">
            {% if hit._highlight and hit._highlight.keyword %}
              <span>{{ hit._highlight.keyword }}</span>
            {% else %}
              <span>{{ hit.keyword }}</span>
            {% endif %}
          </div>
        {% endfor %}
      </div>
    {% endif %}
    {% if hasProducts %}
      <div class="ns-autocomplete-products">
        <div class="ns-autocomplete-header">
          Products
        </div>
        {% for hit in response.products.hits %}
          <a
            class="ns-autocomplete-product"
            href="{{ hit.url }}"
            data-ns-hit="{{ hit | json | escape }}"
            data-testid="product">
            <img
              class="ns-autocomplete-product-image"
              src="{% if hit.imageUrl %}{{ hit.imageUrl }}{% else %}{{ imagePlaceHolder }}{% endif %}"
              alt="{{ hit.name }}"
              width="60"
              height="40" />
            <div class="ns-autocomplete-product-info">
              {% if hit.brand %}
                <div class="ns-autocomplete-product-brand">
                  {{ hit.brand }}
                </div>
              {% endif %}
              <div class="ns-autocomplete-product-name">
                {{ hit.name }}
              </div>
              <div>
                <span>
                  {{ hit.price }}&euro;
                </span>
                {% if hit.listPrice and hit.listPrice != hit.price %}
                    <span class="ns-autocomplete-product-list-price">
                        {{ hit.listPrice }}
                        &euro;
                    </span>
                {% endif %}
              </div>
            </div>
          </a>
        {% endfor %}
      </div>
    {% endif %}
    <div class="ns-autocomplete-submit">
      <button type="submit" class="ns-autocomplete-button">
        See all search results
      </button>
    </div>
  {% endif %}
</div>
`;

function fromLiquidTemplate(template) {
  const instance = Liquid ? new Liquid() : undefined;
  if (instance === undefined) {
    throw new Error(
      "Liquid is not defined. Please include the Liquid library in your page."
    );
  }
  return (container, state) => {
    container.innerHTML = instance.parseAndRenderSync(template, state);
    return Promise.resolve(undefined);
  };
}
function fromRemoteLiquidTemplate(url) {
  return async (container, state) => {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch remote liquid template: ${response.statusText}`);
    }
    const template = await response.text();
    await fromLiquidTemplate(template)(container, state);
  };
}

function d() {
  window.nostojs = window.nostojs ?? function(t) {
    (window.nostojs.q = window.nostojs.q ?? []).push(t);
  };
}
async function m(t) {
  return window.nostojs(t);
}
d();

function getNostoClient() {
  return new Promise(m);
}
const logger = {
  error: (...args) => log("error", ...args),
  warn: (...args) => log("warn", ...args),
  info: (...args) => log("info", ...args),
  debug: (...args) => log("debug", ...args)
};
function log(level, ...args) {
  m((api) => api.internal.logger[level](...args));
}

const defaultConfig = {
  defaultCurrency: "EUR",
  defaultLocale: "en-US",
  /** @hidden  */
  currencySettings: {}
};
const currencyLocales = {
  EUR: "de-DE",
  GBP: "en-GB",
  USD: "en-US",
  AUD: "en-AU",
  CAD: "en-CA",
  //India, Afghanistan, Bangladesh, Bhutan, Myanmar, Nepal, and Pakistan uses lakhs and crores notation
  INR: "en-IN",
  AFN: "en-IN",
  BDT: "en-IN",
  BTN: "en-IN",
  MMK: "en-IN",
  NPR: "en-IN",
  PKR: "en-IN"
};
function getCurrencyFormatting(overrides = {}) {
  const config = {
    ...defaultConfig,
    ...overrides
  };
  if (!overrides.currencySettings) {
    m((api) => {
      config.currencySettings = api.internal.getSettings().currencySettings;
    });
  }
  function formatCurrency(value, currency) {
    const { defaultCurrency, currencySettings, defaultLocale } = config;
    const currencyCode = currency ?? defaultCurrency;
    const locale = currencyLocales[currencyCode] ?? defaultLocale;
    if (currencyCode in currencySettings) {
      const settings = currencySettings[currencyCode];
      const result = new Intl.NumberFormat(locale, {
        useGrouping: !!settings.groupingSeparator,
        minimumFractionDigits: settings.decimalPlaces,
        maximumFractionDigits: settings.decimalPlaces
      }).formatToParts(value);
      const normalised = result.map((it) => {
        if (it.type === "group") return settings.groupingSeparator;
        if (it.type === "decimal") return settings.decimalCharacter;
        return it.value;
      }).join("");
      if (settings?.currencyBeforeAmount) {
        return `${settings.currencyToken}${normalised}`;
      }
      return `${normalised}${settings?.currencyToken}`;
    }
    const numberFormat = new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode
    });
    return numberFormat.format(value);
  }
  return {
    formatCurrency
  };
}

function priceDecorator(config) {
  const { formatCurrency } = getCurrencyFormatting(config);
  function formatPrices(obj, currency) {
    const formatted = {};
    if (obj.price !== undefined) {
      formatted.priceText = formatCurrency(obj.price, currency);
    }
    if (obj.listPrice !== undefined) {
      formatted.listPriceText = formatCurrency(obj.listPrice, currency);
    }
    return Object.assign({}, obj, formatted);
  }
  function hasPrices(obj) {
    return obj.price !== undefined || obj.listPrice !== undefined;
  }
  return function decorator(hit) {
    if (!hasPrices(hit)) {
      return hit;
    }
    const copy = formatPrices(hit, hit.priceCurrencyCode);
    if (copy.skus && copy.skus.some(hasPrices)) {
      copy.skus = copy.skus.map((sku) => {
        return !hasPrices(sku) ? sku : formatPrices(sku, hit.priceCurrencyCode);
      });
    }
    return copy;
  };
}

async function search$1(query, { hitDecorators, ...options } = {}) {
  const api = await new Promise(m);
  if (hitDecorators?.length) {
    return applyDecorators(await api.search(query, options), hitDecorators);
  }
  return await api.search(query, options);
}
function applyDecorators(response, decorators) {
  if (!response.products) {
    return response;
  }
  const decorator = (product) => {
    return decorators.reduce((acc, decorator2) => {
      return decorator2(acc);
    }, product);
  };
  return {
    ...response,
    products: {
      ...response.products,
      hits: response.products.hits.map(decorator)
    }
  };
}

const defaultProductFields = [
  "productId",
  "url",
  "name",
  "imageUrl",
  "thumbUrl",
  "description",
  "brand",
  "variantId",
  "availability",
  "price",
  "priceText",
  "categoryIds",
  "categories",
  "customFields.key",
  "customFields.value",
  "priceCurrencyCode",
  "datePublished",
  "listPrice",
  "unitPricingBaseMeasure",
  "unitPricingUnit",
  "unitPricingMeasure",
  "googleCategory",
  "gtin",
  "ageGroup",
  "gender",
  "condition",
  "alternateImageUrls",
  "ratingValue",
  "reviewCount",
  "inventoryLevel",
  "skus.id",
  "skus.name",
  "skus.price",
  "skus.listPrice",
  "skus.priceText",
  "skus.url",
  "skus.imageUrl",
  "skus.inventoryLevel",
  "skus.customFields.key",
  "skus.customFields.value",
  "skus.availability",
  "pid",
  "onDiscount",
  "extra.key",
  "extra.value",
  "saleable",
  "available",
  "tags1",
  "tags2",
  "tags3"
];
async function search(query, options) {
  const { redirect = false, track, isKeyword = false, hitDecorators } = options ?? {};
  const fields = query.products?.fields ?? defaultProductFields;
  const facets = query.products?.facets ?? ["*"];
  const size = query.products?.size ?? 20;
  const from = query.products?.from ?? 0;
  const response = await search$1(
    {
      ...query,
      products: {
        ...query.products,
        fields,
        facets,
        size,
        from
      }
    },
    { redirect, track, isKeyword, hitDecorators }
  );
  return { query, response };
}

const defaultGaConfig = {
  serpPath: "/search",
  queryParamName: "query",
  enabled: true
};
function getDefaultConfig() {
  return {
    minQueryLength: 2,
    historyEnabled: true,
    historySize: 5,
    hitDecorators: [],
    nostoAnalytics: true,
    googleAnalytics: defaultGaConfig,
    submit: (query, config, options) => {
      if (query.length >= (config.minQueryLength ?? getDefaultConfig().minQueryLength)) {
        search(
          {
            query
          },
          {
            redirect: true,
            track: config.nostoAnalytics ? "serp" : undefined,
            hitDecorators: config.hitDecorators,
            ...options
          }
        );
      }
    }
  };
}

function createDropdown(container, initialState, render, submit, updateInput, onClickBindings) {
  let elements = [];
  let unbindCallbacks = [];
  let isEmpty = true;
  let selectedIndex = -1;
  function handleElementSubmit(el) {
    const hit = el?.dataset?.nsHit;
    if (hit) {
      const parsedHit = parseHit(hit);
      hide();
      if (parsedHit?.item) {
        submit(parsedHit.item);
        return;
      }
      if (parsedHit?.keyword) {
        submit(parsedHit.keyword, {
          redirect: !!parsedHit?._redirect,
          isKeyword: true
        });
        if (parsedHit?._redirect) {
          location.href = parsedHit._redirect;
        }
        return;
      }
      if (parsedHit?.url) {
        location.href = parsedHit.url;
      }
    }
  }
  function loadElements() {
    isEmpty = !container.innerHTML.trim();
    if (!isEmpty) {
      elements = Array.from(
        container.querySelectorAll("[data-ns-hit]")
      ).map((el) => {
        bindElementSubmit(el);
        return el;
      });
    }
  }
  function bindDataCallbacks() {
    Object.entries(onClickBindings).map(([key, callback]) => {
      const dataKey = `[data-ns-${key.replace(/([A-Z])/g, "-$1").toLowerCase()}]`;
      Array.from(container.querySelectorAll(dataKey)).map((el) => {
        const data = el?.dataset?.[`ns${key.charAt(0).toUpperCase() + key.slice(1)}`];
        const onClick = () => {
          callback({
            data,
            el,
            update
          });
        };
        el.addEventListener("click", onClick);
        unbindCallbacks.push(() => {
          el.removeEventListener("click", onClick);
        });
      });
    });
  }
  function bindElementSubmit(el) {
    const onSubmit = () => {
      handleElementSubmit(el);
    };
    el.addEventListener("click", onSubmit);
    unbindCallbacks.push(() => {
      el.removeEventListener("click", onSubmit);
    });
  }
  function highlight(index, prevIndex) {
    if (typeof prevIndex === "number" && elements[prevIndex]) {
      elements[prevIndex].classList.remove("selected");
    }
    if (typeof index === "number" && elements[index]) {
      elements[index]?.classList.add("selected");
      const hit = elements[index]?.dataset?.nsHit;
      if (hit) {
        const parsedHit = parseHit(hit);
        if (parsedHit.item) {
          updateInput(parsedHit.item);
          return;
        }
        if (parsedHit.keyword) {
          updateInput(parsedHit.keyword);
          return;
        }
      }
    }
  }
  function dispose() {
    resetHighlight();
    elements = [];
    unbindCallbacks.forEach((v) => v());
    unbindCallbacks = [];
  }
  async function update(state) {
    dispose();
    await Promise.resolve(render(container, state));
    setTimeout(() => {
      loadElements();
      bindDataCallbacks();
      show();
    }, 0);
  }
  function hide() {
    resetHighlight();
    container.style.display = "none";
  }
  function show() {
    if (!isEmpty) {
      container.style.display = "";
    } else {
      hide();
    }
  }
  function clear() {
    dispose();
    isEmpty = true;
    hide();
  }
  function isOpen() {
    return container.style.display !== "none";
  }
  function goDown() {
    let prevIndex = selectedIndex;
    if (selectedIndex === elements.length - 1) {
      selectedIndex = 0;
    } else {
      prevIndex = selectedIndex++;
    }
    highlight(selectedIndex, prevIndex);
  }
  function goUp() {
    if (hasHighlight()) {
      let prevIndex = selectedIndex;
      if (selectedIndex === 0) {
        selectedIndex = elements.length - 1;
      } else {
        prevIndex = selectedIndex--;
      }
      highlight(selectedIndex, prevIndex);
    } else {
      selectedIndex = elements.length - 1;
      highlight(selectedIndex);
    }
  }
  function handleSubmit() {
    if (isOpen() && hasHighlight() && elements[selectedIndex]) {
      handleElementSubmit(elements[selectedIndex]);
    }
  }
  function hasHighlight() {
    return selectedIndex > -1;
  }
  function getHighlight() {
    return elements[selectedIndex];
  }
  function resetHighlight() {
    if (hasHighlight()) {
      elements[selectedIndex]?.classList.remove("selected");
      selectedIndex = -1;
    }
  }
  function destroy() {
    dispose();
    isEmpty = true;
    container.innerHTML = "";
  }
  async function init() {
    const state = await Promise.resolve(initialState);
    await Promise.resolve(render(container, state));
    setTimeout(() => {
      loadElements();
      bindDataCallbacks();
      hide();
    }, 0);
  }
  init();
  return {
    update,
    clear,
    isOpen,
    goDown,
    goUp,
    handleSubmit,
    destroy,
    show,
    hide,
    resetHighlight,
    hasHighlight,
    getHighlight,
    container
  };
}
function parseHit(hit) {
  try {
    const parsedHit = JSON.parse(hit);
    return parsedHit ?? {};
  } catch (error) {
    logger.warn("Could not parse hit", error);
    return {};
  }
}

class CancellableError extends Error {
}
function makeCancellable(promise) {
  let hasCanceled_ = false;
  const wrappedPromise = new Promise((resolve, reject) => {
    return promise.then(
      (val) => hasCanceled_ ? reject(new CancellableError("cancelled promise")) : resolve(val),
      (error) => hasCanceled_ ? reject(new CancellableError("cancelled promise")) : reject(error)
    );
  });
  return {
    promise: wrappedPromise,
    cancel() {
      hasCanceled_ = true;
    }
  };
}

function getStateActions({
  config,
  history,
  input
}) {
  let cancellable;
  const fetchState = (value, config2, options) => {
    if (typeof config2.fetch === "function") {
      return config2.fetch(value);
    } else {
      return search(
        {
          query: value,
          ...config2.fetch
        },
        {
          track: config2.nostoAnalytics ? "autocomplete" : undefined,
          redirect: false,
          hitDecorators: config2.hitDecorators,
          ...options
        }
      );
    }
  };
  function getHistoryState(query) {
    return Promise.resolve({
      query: {
        query
      },
      history: history?.getItems()
    });
  }
  function updateState(inputValue, options) {
    cancellable?.cancel();
    if (inputValue && inputValue.length >= config.minQueryLength) {
      cancellable = makeCancellable(fetchState(inputValue, config, options));
      return cancellable.promise;
    } else if (history) {
      return getHistoryState(inputValue ?? "");
    }
    return (
      // @ts-expect-error type mismatch
      cancellable?.promise ?? Promise.resolve({})
    );
  }
  function addHistoryItem(item) {
    if (history) {
      history.add(item);
    }
    return getHistoryState(input.value);
  }
  function removeHistoryItem(item) {
    if (history) {
      history.remove(item);
    }
    return getHistoryState(input.value);
  }
  function clearHistory() {
    if (history) {
      history.clear();
    }
    return getHistoryState(input.value);
  }
  return {
    updateState,
    addHistoryItem,
    removeHistoryItem,
    clearHistory
  };
}

function findAll(selector, filterType) {
  const elements = typeof selector === "string" ? Array.from(document.querySelectorAll(selector)) : [selector];
  return elements.filter(
    (v) => filterType ? v instanceof filterType : true
  );
}
function parents(target, selector) {
  let parentList = [];
  findAll(target).forEach((element) => {
    const parent = element.parentNode;
    if (parent !== document && parent instanceof Element) {
      parentList.push(parent);
      parentList = parentList.concat(parents(parent));
    }
  });
  return parentList.filter(
    (element) => selector === undefined
  );
}
function bindClickOutside([element, input], callback) {
  const onClick = (event) => {
    const target = event.target;
    if (target instanceof HTMLElement && element) {
      if (target !== element && target !== input && !parents(target).includes(element)) {
        callback();
      }
    }
  };
  document.addEventListener("click", onClick);
  return {
    destroy: () => {
      document.removeEventListener("click", onClick);
    }
  };
}

function bindInput(selector, callbacks) {
  const target = selector instanceof HTMLInputElement ? [selector] : findAll(selector, HTMLInputElement);
  const unbindCallbacks = target.flatMap((el) => {
    const cbs = [];
    {
      const onKeyDown = (event) => {
        callbacks.onKeyDown?.(el.value, event.key);
        if (event.key === "ArrowDown" || event.key === "ArrowUp" || event.key === "Enter") {
          event.preventDefault();
        }
      };
      el.addEventListener("keydown", onKeyDown);
      cbs.push(() => {
        el.removeEventListener("keydown", onKeyDown);
      });
      const form = el.form;
      if (form) {
        const onSubmit = (event) => {
          event.preventDefault();
          callbacks.onSubmit?.(el.value);
        };
        form.addEventListener("submit", onSubmit);
        cbs.push(() => {
          form?.removeEventListener("submit", onSubmit);
        });
        const buttons = Array.from(form.querySelectorAll("[type=submit]"));
        buttons.forEach((button) => {
          const onClick = (event) => {
            event.preventDefault();
            callbacks.onSubmit?.(el.value);
          };
          button.addEventListener("click", onClick);
          cbs.push(() => {
            button.removeEventListener("click", onClick);
          });
        });
      }
    }
    {
      const onClick = () => {
        callbacks.onClick?.(el.value);
      };
      el.addEventListener("click", onClick);
    }
    {
      const onFocus = () => {
        callbacks.onFocus?.(el.value);
      };
      el.addEventListener("focus", onFocus);
    }
    {
      const onInput = () => {
        callbacks.onInput?.(el.value);
      };
      el.addEventListener("input", onInput);
      cbs.push(() => {
        el.removeEventListener("input", onInput);
      });
    }
    return cbs;
  });
  return {
    destroy() {
      unbindCallbacks.forEach((v) => v());
    }
  };
}

class LimiterError extends Error {
}
function createLimiter(interval, noLimitCount) {
  let currentNumber = 0;
  let lastCompletedNumber = 0;
  let events = [];
  let timeout;
  let lastEvent;
  function limited(getPromise) {
    return new Promise((resolve, reject) => {
      currentNumber += 1;
      const event = {
        getPromise,
        resolve,
        reject,
        number: currentNumber
      };
      removeOldEvents();
      if (events.length < noLimitCount) {
        execute(event);
      } else {
        if (lastEvent !== undefined) {
          lastEvent.reject(new LimiterError("rate limit exceeded"));
        }
        lastEvent = event;
        if (timeout === undefined) {
          timeout = setTimeout(() => {
            timeoutAction();
          }, interval);
        }
      }
    });
  }
  function stop() {
    if (timeout !== undefined) {
      clearTimeout(timeout);
    }
    timeout = undefined;
  }
  function removeOldEvents() {
    const t = (/* @__PURE__ */ new Date()).getTime() - interval * noLimitCount;
    events = events.filter((v) => v >= t);
  }
  function timeoutAction() {
    if (lastEvent !== undefined) {
      execute(lastEvent);
      lastEvent = undefined;
      stop();
    }
  }
  async function execute(event) {
    events.push((/* @__PURE__ */ new Date()).getTime());
    if (event.getPromise !== undefined) {
      try {
        const result = await event.getPromise();
        if (event.number > lastCompletedNumber) {
          lastCompletedNumber = event.number;
          event.resolve(result);
        } else {
          event.reject(new LimiterError("Got newer event"));
        }
      } catch (e) {
        event.reject(e);
      }
    } else {
      event.resolve();
    }
  }
  return {
    limited
  };
}

const localStorageKey = "nostoAutocomplete:gaEvent";
function trackGaPageView(options) {
  const {
    delay = false,
    title = document.title,
    location: location2 = window.location.href
  } = options || {};
  if (delay) {
    saveToLocalStorage(title, location2);
  } else {
    if ("gtag" in window && typeof window.gtag === "function") {
      const accounts = "google_tag_manager" in window && typeof window.google_tag_manager === "object" ? Object.keys(window.google_tag_manager || []).filter((e) => {
        return e.substring(0, 2) == "G-";
      }) : [];
      if (accounts.length > 1) {
        for (let i = 0; i < accounts.length; i++) {
          window.gtag("event", "page_view", {
            page_title: title,
            page_location: location2,
            send_to: accounts[i]
          });
        }
      } else {
        window.gtag("event", "page_view", {
          page_title: title,
          page_location: location2
        });
      }
    }
    if ("ga" in window && typeof window.ga === "function" && "getAll" in window.ga && typeof window.ga.getAll === "function") {
      try {
        const url = new URL(location2);
        const trackers = window.ga.getAll();
        if (trackers?.length > 0) {
          trackers[0]?.send("pageview", url.pathname + url.search);
        }
      } catch (error) {
        logger.warn("Could not send pageview to GA", error);
      }
    }
  }
}
const isGaEnabled = (config) => typeof config.googleAnalytics === "boolean" ? config.googleAnalytics : typeof config.googleAnalytics === "object" && config.googleAnalytics.enabled;
const getGaTrackUrl = (value, config) => {
  const gaConfig = isGaEnabled(config) ? typeof config.googleAnalytics === "boolean" ? defaultGaConfig : {
    ...defaultGaConfig,
    ...config.googleAnalytics
  } : undefined;
  if (value && gaConfig) {
    try {
      return new URL(
        `${gaConfig?.serpPath || location.pathname}?${`${encodeURIComponent(
          gaConfig.queryParamName
        )}=${encodeURIComponent(value).replace(/%20/g, "+")}`}`,
        window.location.origin
      ).toString();
    } catch (error) {
      logger.warn("Could not create track url", error);
      return undefined;
    }
  }
};
function saveToLocalStorage(title, location2) {
  localStorage.setItem(localStorageKey, JSON.stringify({ title, location: location2 }));
}
function consumeLocalStorageEvent() {
  const eventString = localStorage.getItem(localStorageKey);
  if (typeof eventString === "string") {
    localStorage.removeItem(localStorageKey);
    try {
      const event = JSON.parse(eventString);
      trackGaPageView(event);
    } catch (e) {
      logger.warn("Could not consume pageView", e);
    }
  }
}
setTimeout(consumeLocalStorageEvent, 1e3);

function createHistory(size) {
  const localStorageKey = "nosto:autocomplete:history";
  let items = get();
  function get() {
    try {
      return JSON.parse(localStorage.getItem(localStorageKey) ?? "[]") ?? [];
    } catch (err) {
      logger.error("Could not get history items.", err);
      return [];
    }
  }
  function set(data) {
    try {
      localStorage.setItem(localStorageKey, JSON.stringify(data));
    } catch (err) {
      logger.error("Could not set history items.", err);
    }
  }
  function add(item) {
    set(
      items = [{ item }, ...items?.filter((v) => v.item !== item) || []].slice(
        0,
        size
      )
    );
  }
  function clear() {
    set(items = []);
  }
  function remove(item) {
    set(items = items.filter((v) => v.item !== item));
  }
  function getItems() {
    return items;
  }
  return {
    add,
    clear,
    remove,
    getItems
  };
}

function autocomplete(config) {
  const fullConfig = {
    ...getDefaultConfig(),
    ...config
  };
  const history = fullConfig.historyEnabled ? createHistory(fullConfig.historySize) : undefined;
  const limiter = createLimiter(300, 1);
  const dropdowns = findAll(config.inputSelector, HTMLInputElement).map(
    (inputElement) => {
      const actions = getStateActions({
        config: fullConfig,
        history,
        input: inputElement
      });
      const dropdown = createInputDropdown({
        input: inputElement,
        config: fullConfig,
        actions
      });
      if (!dropdown) {
        return;
      }
      const input = bindInput(inputElement, {
        onInput: async (value) => {
          try {
            await limiter.limited(async () => {
              const state = await actions.updateState(value);
              dropdown.update(state);
            });
          } catch (err) {
            if (!(err instanceof LimiterError || err instanceof CancellableError)) {
              throw err;
            }
          }
        },
        onClick() {
          dropdown.resetHighlight();
        },
        onFocus() {
          dropdown.show();
        },
        onBlur() {
          dropdown.hide();
        },
        onSubmit() {
          submitWithContext({
            config: fullConfig,
            actions
          })(inputElement.value);
          dropdown.hide();
        },
        onKeyDown(_, key) {
          if (key === "Escape") {
            dropdown.hide();
          } else if (key === "ArrowDown") {
            if (dropdown.isOpen()) {
              dropdown.goDown();
            } else {
              dropdown.show();
            }
          } else if (key === "ArrowUp") {
            if (dropdown.isOpen()) {
              dropdown.goUp();
            }
          } else if (key === "Enter") {
            if (dropdown.isOpen() && dropdown.hasHighlight()) {
              const data = dropdown.getHighlight()?.dataset?.nsHit;
              if (data) {
                trackClick({
                  config: fullConfig,
                  data,
                  query: inputElement.value
                });
              }
              dropdown.handleSubmit();
            } else {
              submitWithContext({
                actions,
                config: fullConfig
              })(inputElement.value);
            }
          }
        }
      });
      const clickOutside = bindClickOutside(
        [dropdown.container, inputElement],
        () => {
          dropdown.hide();
        }
      );
      return {
        open() {
          dropdown.show();
        },
        close() {
          dropdown.hide();
        },
        destroy() {
          input.destroy();
          clickOutside.destroy();
          dropdown.destroy();
        }
      };
    }
  );
  return {
    destroy() {
      dropdowns.forEach((dropdown) => dropdown?.destroy());
    },
    open() {
      dropdowns.forEach((dropdown) => dropdown?.open());
    },
    close() {
      dropdowns.forEach((dropdown) => dropdown?.close());
    }
  };
}
function createInputDropdown({
  input,
  config,
  actions
}) {
  const dropdownElements = typeof config.dropdownSelector === "function" ? findAll(config.dropdownSelector(input), HTMLElement) : findAll(config.dropdownSelector, HTMLElement);
  if (dropdownElements.length === 0) {
    logger.error(`No dropdown element found for input ${input}`);
    return;
  } else if (dropdownElements.length > 1) {
    logger.error(
      `Multiple dropdown elements found for input ${input}, using the first element`
    );
  }
  const dropdownElement = dropdownElements[0];
  return createDropdown(
    dropdownElement,
    actions.updateState(input.value),
    config.render,
    submitWithContext({
      actions,
      config
    }),
    (value) => input.value = value,
    {
      removeHistory: async function({ data, update }) {
        if (data === "all") {
          const state = await actions.clearHistory();
          return update(state);
        } else if (data) {
          const state = await actions.removeHistoryItem(data);
          return update(state);
        }
      },
      hit: function({ data }) {
        if (data) {
          trackClick({ config, data, query: input.value });
        }
      }
    }
  );
}
async function trackClick({
  config,
  data,
  query
}) {
  if (!config.googleAnalytics && !config.nostoAnalytics) {
    return;
  }
  const parsedHit = parseHit(data);
  if (config.nostoAnalytics) {
    if (parsedHit) {
      const api = await getNostoClient();
      api?.recordSearchClick?.("autocomplete", parsedHit);
    }
  }
  if (isGaEnabled(config)) {
    if (parsedHit._redirect) {
      trackGaPageView({
        delay: true,
        location: getGaTrackUrl(parsedHit.keyword, config)
      });
    }
    if (parsedHit.url) {
      trackGaPageView({
        delay: true,
        location: getGaTrackUrl(query, config)
      });
    }
  }
}
function submitWithContext(context) {
  return async (value, options) => {
    const { config, actions } = context;
    const { redirect = false } = options ?? {};
    if (value.length > 0) {
      if (config.historyEnabled) {
        actions.addHistoryItem(value);
      }
      if (config.nostoAnalytics) {
        const api = await getNostoClient();
        api?.recordSearchSubmit?.(value);
      }
      if (isGaEnabled(config)) {
        trackGaPageView({
          delay: true,
          location: getGaTrackUrl(value, config)
        });
      }
      if (!redirect && typeof config?.submit === "function") {
        config.submit(value, config, options);
      }
    }
  };
}

export { autocomplete, defaultLiquidTemplate, fromLiquidTemplate, fromRemoteLiquidTemplate, priceDecorator, search };
//# sourceMappingURL=autocomplete.mjs.map
